/**
 * Stock Visibility Utilities
 * Manages product visibility based on purchase history
 */

import { sameProductId } from './productIdUtils';
import { flattenPurchaseLineItems } from './lotSystem';
import { isRestaurantDishProduct } from './restaurantMode';

function isStockEligibleProduct(product) {
  if (!product) return false;
  if (isRestaurantDishProduct(product)) return false;
  if (product.trackInventory === false) return false;
  return true;
}

/**
 * Check if a product has been purchased (exists in stock)
 * @param {string} productId - The product ID
 * @param {Object|Array} lots - The lots data (can be object or array)
 * @param {Array} purchases - The purchases data
 * @returns {boolean} - True if product has been purchased
 */
export const hasProductBeenPurchased = (productId, lots, purchases) => {
  if (!productId) {
    return false;
  }

  // Handle both array and object formats for lots
  let lotsArray = [];
  if (Array.isArray(lots)) {
    lotsArray = lots;
  } else if (lots && typeof lots === 'object') {
    // If it's an object, check if it's keyed by product ID or if it contains arrays
    if (lots[productId] && Array.isArray(lots[productId])) {
      lotsArray = lots[productId];
    } else {
      // Convert object values to array
      lotsArray = Object.values(lots);
    }
  }

  // Check if product has lots (indicating it was purchased or transferred)
  if (lotsArray.length > 0) {
    const hasProductLots = lotsArray.some(
      (lot) => lot && sameProductId(lot.productId, productId)
    );
    if (hasProductLots) {
      return true;
    }
  }

  // Check if product exists in purchases (flat lines + nested products/items)
  const purchaseLines = flattenPurchaseLineItems(purchases);
  if (purchaseLines.some((line) => sameProductId(line.productId, productId))) {
    return true;
  }

  return false;
};

/**
 * Get products that have been purchased
 * @param {Array} products - Array of all products
 * @param {Object} lots - The lots data
 * @param {Array} purchases - The purchases data
 * @returns {Array} - Array of purchased products
 */
export const getPurchasedProducts = (products, lots, purchases) => {
  if (!products || !Array.isArray(products)) {
    return [];
  }

  // Flatten purchases once — not per product (was O(products × purchaseLines))
  const purchaseLines = flattenPurchaseLineItems(purchases);
  const purchasedIds = new Set();

  let lotsArray = [];
  if (Array.isArray(lots)) {
    lotsArray = lots;
  } else if (lots && typeof lots === 'object') {
    lotsArray = Object.values(lots).flatMap((v) => (Array.isArray(v) ? v : [v]));
  }
  for (const lot of lotsArray) {
    if (lot?.productId != null) purchasedIds.add(String(lot.productId));
  }
  for (const line of purchaseLines) {
    if (line?.productId != null) purchasedIds.add(String(line.productId));
  }

  if (purchasedIds.size === 0) return [];

  return products.filter(
    (product) =>
      isStockEligibleProduct(product) && purchasedIds.has(String(product.id))
  );
};

/**
 * Get products that have NOT been purchased
 * @param {Array} products - Array of all products
 * @param {Object} lots - The lots data
 * @param {Array} purchases - The purchases data
 * @returns {Array} - Array of products that have NOT been purchased
 */
export const getUnpurchasedProducts = (products, lots, purchases) => {
  if (!products || !Array.isArray(products)) {
    return [];
  }

  return products.filter(
    (product) =>
      isStockEligibleProduct(product) &&
      !hasProductBeenPurchased(product.id, lots, purchases)
  );
};

/**
 * Get products that should be visible in stock page
 * @param {Array} products - Array of all products
 * @param {Object} lots - The lots data
 * @param {Array} purchases - The purchases data
 * @param {string} viewMode - 'all', 'purchased', 'unpurchased'
 * @returns {Array} - Array of products based on view mode
 */
export const getVisibleProducts = (products, lots, purchases, viewMode = 'purchased') => {
  if (!products || !Array.isArray(products)) {
    return [];
  }

  switch (viewMode) {
    case 'purchased':
      return getPurchasedProducts(products, lots, purchases);
    case 'unpurchased':
      return getUnpurchasedProducts(products, lots, purchases);
    case 'all':
    default:
      return products.filter(isStockEligibleProduct);
  }
};

/**
 * Check if Achat vs Bénéfice report should be available
 * @param {Array} sales - The sales data
 * @returns {boolean} - True if sales have occurred
 */
export const canShowAchatVsBeneficeReport = (sales) => {
  if (!sales || !Array.isArray(sales)) {
    return false;
  }

  return sales.length > 0;
};
